import styled, { css, keyframes } from 'styled-components';

const fadeSlideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(12px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const slideInDown = keyframes`
    from {
        opacity: 0;
        transform: translateY(-8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const expandIn = keyframes`
    from {
        opacity: 0;
        max-height: 0;
        transform: translateY(-4px);
    }
    to {
        opacity: 1;
        max-height: 640px;
        transform: translateY(0);
    }
`;

const ctaPress = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(0.98); }
    100% { transform: scale(1); }
`;

const motionSafe = (styles: ReturnType<typeof css>) => css`
    @media (prefers-reduced-motion: no-preference) {
        ${styles}
    }
`;

export const FormItemWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    background: #f7f8fa;
    min-height: 100vh;
`;

export const FormGridWrapper = styled.div`
    display: grid;
    grid-template-rows: auto 1fr auto;
    width: 100%;
    min-height: 100vh;
    background: #ffffff;

    @media screen and (min-width: 768px) {
        max-width: 480px;
        margin: 0 auto;
        box-shadow: 0 0 0 1px #e8e8ef;
    }
`;

export const HeaderWrapper = styled.div`
    display: flex;
    padding: 20px;
    padding-bottom: 0;
`;

export const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        width: 0;
        height: 0;
    }
    padding: 0 20px 24px;
    ${motionSafe(css`
        animation: ${fadeSlideUp} 0.35s ease-out;
    `)}
`;

export const Title = styled.div`
    color: #040222;
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
    margin: 10px 0 4px;
`;

export const SectionBlock = styled.section<{ $animateExpand?: boolean }>`
    display: flex;
    flex-direction: column;
    ${(props) =>
        props.$animateExpand &&
        motionSafe(css`
            animation: ${expandIn} 0.28s ease-out;
            overflow: hidden;
        `)}
`;

export const SectionTitle = styled.h2`
    display: flex;
    color: #040222;
    font-size: 16px;
    font-weight: 600;
    margin: 18px 0 4px;
`;

export const ItemWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 14px 0;
`;

export const ConfirmRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 0;
    border-bottom: 1px solid #eef0f4;

    &:last-child {
        border-bottom: none;
    }
`;

export const ConfirmLabel = styled.div`
    color: #5b5675;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
`;

export const ConfirmValue = styled.div`
    color: #040222;
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
    word-break: break-word;
`;

export const GSTTitle = styled.div`
    display: flex;
    color: #151619;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 16px;
`;

export const CheckBoxText = styled.div`
    display: flex;
    color: #040222;
    font-size: 14px;
    line-height: 20px;
`;

export const CheckBoxFlex = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
`;

export const CheckBoxItemWrapper = styled.div`
    display: flex;
    gap: 48px;
`;

export const BankDetailsTitle = styled.div`
    display: flex;
    color: #040222;
    font-size: 16px;
    font-weight: 600;
    margin-top: 10px;
`;

export const BottomWrapper = styled.div`
    display: flex;
    flex-direction: column;
    background: white;
    border-top: 1px solid #e0e0e8;
    padding: 15px 20px;
    position: sticky;
    bottom: 0;
    z-index: 2;
`;

export const TncWrapper = styled.div`
    display: flex;
    gap: 10px;
`;

export const TcnText = styled.span`
    color: #040222;
    font-size: 12px;
    line-height: 16px;

    & > span {
        color: #1c73e8;
        cursor: pointer;
    }
`;

export const ButtonWrapper = styled.div<{ $pressed?: boolean }>`
    margin-top: 16px;
    ${(props) =>
        props.$pressed &&
        motionSafe(css`
            animation: ${ctaPress} 0.2s ease;
        `)}
`;

export const DocumentUploadErrorMessage = styled.div`
    display: flex;
    font-size: 12px;
    line-height: 16px;
    margin-top: 4px;
    color: #d83d37;
`;

export const PartialModalContent = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: stretch;
`;

export const PartialModalTitle = styled.div`
    font-size: 18px;
    line-height: 24px;
    font-weight: 700;
    color: #7a4d00;
    margin-bottom: 12px;
`;

export const PartialModalText = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 22px;
    font-weight: 500;
    color: #53556b;
`;

export const PartialModalHint = styled.p`
    margin: 12px 0 0;
    font-size: 13px;
    line-height: 18px;
    color: #8a6420;
`;

export const PartialModalActions = styled.div`
    margin-top: 24px;
    width: 100%;

    button {
        width: 100%;
    }
`;

export const ErrorBanner = styled.div`
    margin: 12px 0 4px;
    padding: 12px 14px;
    border-radius: 10px;
    background: #fef2f1;
    border: 1px solid #f0b8b4;
    color: #a12822;
    font-size: 13px;
    line-height: 18px;
    ${motionSafe(css`
        animation: ${slideInDown} 0.28s ease-out;
    `)}
`;

export const LoadingState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
    color: #5b5675;
    font-size: 14px;
`;

/* legacy aliases used by TNC screen */
export { fadeSlideUp };
